import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Avatar,
  Typography,
  Divider,
  Box,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  Stack,
  IconButton,
  Skeleton,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  Tabs,
  Tab,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import NotesIcon from "@mui/icons-material/Notes";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";

import api, { API_BASE_URL } from "../../utils/api";

const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : "N/A");
const trimOrNA = (v) => (v && String(v).trim() ? String(v).trim() : "N/A");
const trimOrNull = (v) => (v && String(v).trim() ? String(v).trim() : null);

const getBaseCode = (clubMemberCode) => {
  const code = String(clubMemberCode || "").trim();
  const m = code.match(/^\d+/);
  return m ? m[0] : code;
};

// robust parser for property-ish fields
const pickFirst = (raw, type = "text") => {
  if (raw == null) return null;

  if (Array.isArray(raw)) {
    const v = raw.map((x) => (x ?? "").toString().trim()).filter(Boolean)[0];
    return v || null;
  }

  let s = raw.toString().trim();
  if (!s) return null;

  const looksJson =
    (s.startsWith("[") && s.endsWith("]")) ||
    (s.startsWith("{") && s.endsWith("}"));

  if (looksJson) {
    try {
      const parsed = JSON.parse(s);

      if (Array.isArray(parsed)) {
        const v = parsed.map((x) => (x ?? "").toString().trim()).filter(Boolean)[0];
        return v || null;
      }

      if (parsed && typeof parsed === "object") {
        const possible =
          parsed.emails ||
          parsed.emails_raw ||
          parsed.email ||
          parsed.phone_numbers ||
          parsed.phones ||
          parsed.phone;

        if (Array.isArray(possible)) {
          const v = possible.map((x) => (x ?? "").toString().trim()).filter(Boolean)[0];
          return v || null;
        }

        if (typeof possible === "string") {
          s = possible.trim();
        }
      }
    } catch {
      // ignore parsing issues
    }
  }

  s = s.replace(/[\[\]"]/g, "").trim();
  const parts = s.split(/[;,|\n]/).map((x) => x.trim()).filter(Boolean);
  if (!parts.length) return null;

  if (type === "email") {
    const found = parts.find((p) => p.includes("@"));
    return found || parts[0] || null;
  }

  if (type === "phone") {
    const first = parts[0].replace(/^[a-zA-Z\s]+:\s*/g, "").trim();
    const cleaned = first.replace(/[^\d+()\-\s]/g, "").trim();
    return cleaned || first || null;
  }

  return parts[0] || null;
};

// normalize property -> member-ish shape
const normalizePropertyToMember = (p, fallbackCode) => {
  const read = (...keys) => {
    for (const k of keys) {
      const v = p?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
    return null;
  };

  const accountId =
    read("account_id", "Account_ID", "AccountId", "accountId") ??
    fallbackCode ??
    read("id", "ID") ??
    "";

  const mailingName = read("mailing_name", "Mailing_Name", "mailingName") || "";

  const rawEmails = read("emails", "Emails", "EMAILS", "email", "Email", "emails_raw");
  const rawPhones = read(
    "phone_numbers",
    "Phone_Numbers",
    "PHONE_NUMBERS",
    "phoneNumbers",
    "phones",
    "Phones"
  );

  const fullAddress =
    [
      read("street_1", "Street_1"),
      read("street_2", "Street_2"),
      read("city", "City"),
      read("state", "State"),
      read("zip", "Zip", "postal_code", "PostalCode"),
    ]
      .map((x) => (x || "").toString().trim())
      .filter(Boolean)
      .join(", ") || null;

  const mailingAddress =
    [
      read("mailing_street_1", "Mailing_Street_1"),
      read("mailing_street_2", "Mailing_Street_2"),
      read("mailing_city", "Mailing_City"),
      read("mailing_state", "Mailing_State"),
      read("mailing_zip", "Mailing_Zip", "mailing_postal_code"),
    ]
      .map((x) => (x || "").toString().trim())
      .filter(Boolean)
      .join(", ") || null;

  const displayName = mailingName.toString().trim() || `Property ${accountId}`.trim();

  const parts = displayName.split(/\s+/).filter(Boolean);
  const first = parts[0] || "Property";
  const last = parts.length > 1 ? parts.slice(1).join(" ") : "";

  return {
    ...p,
    account_id: String(accountId),
    ClubMemberCode: String(accountId),

    EmailAddress: pickFirst(rawEmails, "email"),
    PhoneNumber: pickFirst(rawPhones, "phone"),
    FullAddress: fullAddress,
    AlternateAddress: mailingAddress,

    FirstName: first,
    LastName: last,

    JoinDate: null,
    AlternatePhoneNumber: null,
    PhotoGraphicFileName: null,

    isPropertyFallback: true,
  };
};

const DetailItem = ({ icon, label, value }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1.25,
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        color: "#8a94a6",
        mt: "2px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>

    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: "#8a94a6",
          fontWeight: 600,
          lineHeight: 1.2,
          mb: 0.35,
          letterSpacing: "0.02em",
        }}
      >
        {label}
      </Typography>

      <Box
        sx={{
          color: "#223142",
          fontSize: "0.95rem",
          fontWeight: 600,
          lineHeight: 1.45,
          wordBreak: "break-word",
          "& a": {
            color: "#223142",
            fontWeight: 600,
            textDecorationColor: "rgba(34,49,66,0.25)",
          },
        }}
      >
        {typeof value === "string" ? (
          <Typography variant="body2">{value}</Typography>
        ) : (
          value
        )}
      </Box>
    </Box>
  </Box>
);

const DependentsTabPanel = ({ baseCode, excludeCode, onPreview }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!baseCode) {
        setRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res = await api.get(
          `/members/${encodeURIComponent(baseCode)}/dependents`,
          {
            params: { excludeCode },
          }
        );

        if (!cancelled) {
          setRows(Array.isArray(res?.data?.dependents) ? res.data.dependents : []);
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.data?.message || e.message || "Failed to load dependents"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [baseCode, excludeCode]);

  if (loading) {
    return (
      <Stack spacing={1}>
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={isXs ? 48 : 56} />
        ))}
      </Stack>
    );
  }

  if (error) {
    return (
      <Typography color="error" variant="body2">
        {error}
      </Typography>
    );
  }

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No household members found.
      </Typography>
    );
  }

  return (
    <List dense={isXs} sx={{ px: 0 }}>
      {rows.map((d) => {
        const first = (d.FirstName || "").toString().trim();
        const last = (d.LastName || "").toString().trim();
        const name = `${first} ${last}`.trim() || d.ClubMemberCode;
        const file = (d.PhotoGraphicFileName || "").toString().trim();
        const avatarSrc = file
          ? `${API_BASE_URL}/images/${encodeURIComponent(file)}`
          : undefined;
        const email = trimOrNull(d.EmailAddress);
        const phone = trimOrNull(d.PhoneNumber);

        return (
          <ListItem
            key={d.ClubMemberCode}
            sx={{
              borderRadius: 2,
              mb: 0.75,
              px: { xs: 0.5, sm: 1 },
              alignItems: "flex-start",
              border: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.02)",
            }}
          >
            <ListItemAvatar sx={{ minWidth: isXs ? 40 : 56 }}>
              <Avatar
                src={avatarSrc}
                alt={name}
                sx={{
                  width: { xs: 32, sm: 40 },
                  height: { xs: 32, sm: 40 },
                  cursor: avatarSrc ? "pointer" : "default",
                }}
                onClick={() => avatarSrc && onPreview?.(avatarSrc, name)}
              >
                {!avatarSrc && (name?.[0] || "?")}
              </Avatar>
            </ListItemAvatar>

            <ListItemText
              primary={
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ rowGap: 0.5 }}
                >
                  <Typography
                    variant={isXs ? "body1" : "subtitle1"}
                    fontWeight={600}
                    sx={{ mr: 0.5 }}
                  >
                    {name}
                  </Typography>
                  <Chip size="small" label={`Code: ${d.ClubMemberCode}`} />
                </Stack>
              }
              secondary={
                <Stack
                  direction="row"
                  spacing={2}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 0.5, "& a, & p": { wordBreak: "break-word" } }}
                >
                  {email ? (
                    <Link href={`mailto:${email}`} underline="hover" variant="body2">
                      {email}
                    </Link>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No email
                    </Typography>
                  )}

                  {phone ? (
                    <Link href={`tel:${phone}`} underline="hover" variant="body2">
                      {phone}
                    </Link>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No phone
                    </Typography>
                  )}
                </Stack>
              }
            />
          </ListItem>
        );
      })}
    </List>
  );
};

const NotesTabPanel = ({ notes = [] }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  if (!notes.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No notes available for this member.
      </Typography>
    );
  }

  return (
    <List dense={isXs} sx={{ px: 0 }}>
      {notes.map((n, i) => (
        <ListItem
          key={i}
          sx={{
            borderRadius: 2,
            mb: 0.75,
            px: { xs: 0.5, sm: 1 },
            alignItems: "flex-start",
            border: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <ListItemAvatar sx={{ minWidth: isXs ? 36 : 48 }}>
            <Avatar sx={{ bgcolor: "info.main", width: 32, height: 32 }}>
              {i + 1}
            </Avatar>
          </ListItemAvatar>

          <ListItemText
            primary={
              <Typography sx={{ fontWeight: 500 }}>
                {n.NoteLine || "No content"}
              </Typography>
            }
          />
        </ListItem>
      ))}
    </List>
  );
};

/**
 * IMPORTANT:
 * Pass fallbackCode={ClubMemberCode} from MemberProfilePage
 */
const MemberDetailsCard = ({ member, notes = [], fallbackCode }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [resolvedMember, setResolvedMember] = useState(member || null);
  const [source, setSource] = useState("props"); // props | member | property
  const [loadingProfile, setLoadingProfile] = useState(false);

  const code =
    trimOrNull(member?.ClubMemberCode) ||
    trimOrNull(member?.account_id) ||
    trimOrNull(fallbackCode) ||
    null;

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (member && member.isPropertyFallback) {
        const normalized = normalizePropertyToMember(
          member,
          code || member.ClubMemberCode
        );
        setResolvedMember(normalized);
        setSource("property");
        return;
      }

      if (member && member.ClubMemberCode) {
        setResolvedMember(member);
        setSource("member");
        return;
      }

      if (!code) {
        setResolvedMember(member || null);
        setSource("props");
        return;
      }

      setLoadingProfile(true);

      try {
        const mRes = await api.get(`/members/${encodeURIComponent(code)}`);
        if (!cancelled) {
          setResolvedMember(mRes.data);
          setSource("member");
        }
      } catch (err) {
        const status = err?.response?.status;

        if (status && status !== 404) {
          if (!cancelled) {
            setResolvedMember(member || null);
            setSource("props");
          }
        } else {
          try {
            const pRes = await api.get(`/properties/${encodeURIComponent(code)}`);
            const normalized = normalizePropertyToMember(pRes.data, code);

            if (!cancelled) {
              setResolvedMember(normalized);
              setSource("property");
            }
          } catch {
            if (!cancelled) {
              setResolvedMember(member || null);
              setSource("props");
            }
          }
        }
      } finally {
        if (!cancelled) setLoadingProfile(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [member, code]);

  const m = resolvedMember || {};

  const joinDate = formatDate(m?.JoinDate);

  const emailResolved =
    trimOrNull(m?.EmailAddress) ??
    pickFirst(m?.emails ?? m?.emails_raw ?? m?.Email ?? m?.Emails, "email");

  const phoneResolved =
    trimOrNull(m?.PhoneNumber) ??
    pickFirst(m?.phone_numbers ?? m?.Phone_Numbers ?? m?.Phones, "phone");

  const email = emailResolved || "N/A";
  const phone = phoneResolved || null;
  const altPhone = trimOrNull(m?.AlternatePhoneNumber);

  const address = trimOrNA(m?.FullAddress);

  const altAddress =
    trimOrNull(m?.AlternateAddress) ??
    (Array.isArray(m?.AlternateAddresses) && m.AlternateAddresses.length
      ? [
          m.AlternateAddresses[0]?.AddressLine1,
          m.AlternateAddresses[0]?.AddressLine2,
          m.AlternateAddresses[0]?.AddressLine3,
          m.AlternateAddresses[0]?.AddressLine4,
          m.AlternateAddresses[0]?.AddressLine5,
        ]
          .map((v) => (v || "").toString().trim())
          .filter(Boolean)
          .join(" ")
      : null);

  const mapsLink =
    address !== "N/A"
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
      : null;

  const altMapsLink = altAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(altAddress)}`
    : null;

  const baseCode = useMemo(() => getBaseCode(m?.ClubMemberCode), [m?.ClubMemberCode]);

  const photoUrl = m?.PhotoGraphicFileName?.trim?.()
    ? `${API_BASE_URL}/images/${encodeURIComponent(m.PhotoGraphicFileName.trim())}`
    : undefined;

  const [preview, setPreview] = useState({ open: false, src: "", title: "" });
  const [tabIndex, setTabIndex] = useState(0);
  const [dependentsCount, setDependentsCount] = useState(null);

  const openPreview = (src, title) =>
    src && setPreview({ open: true, src, title });

  const closePreview = () => setPreview((p) => ({ ...p, open: false }));

  useEffect(() => {
    let cancelled = false;

    async function loadCount() {
      if (!baseCode) {
        setDependentsCount(0);
        return;
      }

      try {
        const res = await api.get(
          `/members/${encodeURIComponent(baseCode)}/dependents`,
          {
            params: { excludeCode: String(m?.ClubMemberCode || "").trim() },
          }
        );

        if (!cancelled) {
          const rows = Array.isArray(res?.data?.dependents)
            ? res.data.dependents
            : [];
          setDependentsCount(rows.length);
        }
      } catch {
        if (!cancelled) setDependentsCount(0);
      }
    }

    loadCount();
    return () => {
      cancelled = true;
    };
  }, [baseCode, m?.ClubMemberCode]);

  const availableTabs = [
    {
      key: "details",
      label: "Details",
      icon: <InfoOutlinedIcon sx={{ fontSize: 18 }} />,
      show: true,
    },
    {
      key: "household",
      label: "Household Members",
      icon: <PeopleAltIcon sx={{ fontSize: 18 }} />,
      show: !!dependentsCount,
      count: dependentsCount,
    },
    {
      key: "notes",
      label: "Notes from Jonas",
      icon: <NotesIcon sx={{ fontSize: 18 }} />,
      show: Array.isArray(notes) && notes.length > 0,
      count: notes?.length || 0,
    },
  ].filter((tab) => tab.show);

  useEffect(() => {
    if (tabIndex > availableTabs.length - 1) {
      setTabIndex(0);
    }
  }, [availableTabs.length, tabIndex]);

  const displayName =
    `${trimOrNull(m?.FirstName) || ""} ${trimOrNull(m?.LastName) || ""}`.trim() ||
    trimOrNull(m?.mailing_name) ||
    `Property ${m?.account_id || m?.ClubMemberCode || ""}`.trim() ||
    "Property";

  const numberLabel = source === "property" ? "Property Number" : "Member Number";
  const numberValue =
    source === "property"
      ? m?.account_id ?? m?.ClubMemberCode ?? "N/A"
      : m?.ClubMemberCode ?? "N/A";

  return (
    <>
      <Card
        sx={{
          width: "100%",
          borderRadius: 3,
          boxShadow: "0 1px 4px rgba(15, 23, 42, 0.08)",
          border: "1px solid #e6ebf2",
          backgroundColor: "#fff",
        }}
      >
        <CardContent
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
          }}
        >
          {loadingProfile && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1.5 }}
            >
              Loading profile…
            </Typography>
          )}

          {source === "property" && (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ display: "block", mb: 1.5 }}
            >
              Loaded from Property record (member not found)
            </Typography>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "220px 1fr" },
              gap: { xs: 2, md: 3 },
              alignItems: "start",
            }}
          >
            <Box
              sx={{
                textAlign: { xs: "center", md: "left" },
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center", md: "flex-start" },
              }}
            >
              <Avatar
                src={photoUrl}
                alt={displayName}
                onClick={() => openPreview(photoUrl, displayName)}
                sx={{
                  width: { xs: 72, sm: 92, md: 112 },
                  height: { xs: 72, sm: 92, md: 112 },
                  mb: 1.5,
                  border: "4px solid rgba(47,99,173,0.12)",
                  boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
                  cursor: photoUrl ? "pointer" : "default",
                }}
              >
                {!photoUrl && (displayName?.[0] || "?")}
              </Avatar>

              <Typography
                variant={isXs ? "h6" : "h5"}
                fontWeight="bold"
                sx={{ wordBreak: "break-word", color: "#223142" }}
              >
                {displayName}
              </Typography>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {numberLabel}: {numberValue}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                sx={{ mt: 1 }}
              >
                <Chip
                  label="Active"
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#2f63ad",
                    backgroundColor: "rgba(47,99,173,0.10)",
                    borderRadius: "999px",
                  }}
                />

                {dependentsCount > 0 && (
                  <Chip
                    label={`${dependentsCount} household`}
                    size="small"
                    variant="outlined"
                  />
                )}

                {notes?.length > 0 && (
                  <Chip
                    label={`${notes.length} notes`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Tabs
                value={tabIndex}
                onChange={(_, value) => setTabIndex(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minHeight: "auto",
                  mb: 2,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "10px",
                    minHeight: 40,
                    color: "#5f6b7a",
                    mr: 1,
                    px: { xs: 1.25, md: 1.75 },
                    py: 1,
                    whiteSpace: "nowrap",
                    minWidth: "fit-content",
                  },
                  "& .Mui-selected": {
                    color: "#2f63ad !important",
                    backgroundColor: "rgba(47,99,173,0.08)",
                  },
                  "& .MuiTabs-indicator": {
                    display: "none",
                  },
                }}
              >
                {availableTabs.map((tab) => (
                  <Tab
                    key={tab.key}
                    label={
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        {tab.icon}
                        <span>{tab.label}</span>
                        {typeof tab.count === "number" && (
                          <Chip
                            label={tab.count}
                            size="small"
                            sx={{ height: 18, fontSize: 10 }}
                          />
                        )}
                      </Stack>
                    }
                  />
                ))}
              </Tabs>

              <Divider sx={{ mb: 2 }} />

              {availableTabs[tabIndex]?.key === "details" && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: { xs: 1.5, sm: 2, md: 2.5 },
                    pr: { md: 1 },
                  }}
                >
                  <DetailItem
                    icon={<EmailIcon sx={{ fontSize: 18 }} />}
                    label="Email"
                    value={
                      email !== "N/A" ? (
                        <Link
                          href={`mailto:${email}`}
                          underline="hover"
                          color="inherit"
                        >
                          {email}
                        </Link>
                      ) : (
                        "N/A"
                      )
                    }
                  />

                  <DetailItem
                    icon={<PhoneIcon sx={{ fontSize: 18 }} />}
                    label="Phone"
                    value={
                      phone ? (
                        <Link
                          href={`tel:${phone}`}
                          underline="hover"
                          color="inherit"
                        >
                          {phone}
                        </Link>
                      ) : (
                        "N/A"
                      )
                    }
                  />

                  <DetailItem
                    icon={<CalendarTodayIcon sx={{ fontSize: 18 }} />}
                    label="Join Date"
                    value={joinDate}
                  />

                  <DetailItem
                    icon={<LocationOnIcon sx={{ fontSize: 18 }} />}
                    label="Address"
                    value={
                      mapsLink ? (
                        <Link
                          href={mapsLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          color="inherit"
                        >
                          {address}
                        </Link>
                      ) : (
                        "N/A"
                      )
                    }
                  />

                  <DetailItem
                    icon={<PhoneAndroidIcon sx={{ fontSize: 18 }} />}
                    label="Alt Phone"
                    value={
                      altPhone ? (
                        <Link
                          href={`tel:${altPhone}`}
                          underline="hover"
                          color="inherit"
                        >
                          {altPhone}
                        </Link>
                      ) : (
                        "N/A"
                      )
                    }
                  />

                  <DetailItem
                    icon={<LocationOnIcon sx={{ fontSize: 18 }} />}
                    label="Alternate Address"
                    value={
                      altMapsLink ? (
                        <Link
                          href={altMapsLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          color="inherit"
                        >
                          {altAddress}
                        </Link>
                      ) : (
                        "N/A"
                      )
                    }
                  />
                </Box>
              )}

              {availableTabs[tabIndex]?.key === "household" && (
                <DependentsTabPanel
                  baseCode={baseCode}
                  excludeCode={String(m?.ClubMemberCode || "").trim()}
                  onPreview={openPreview}
                />
              )}

              {availableTabs[tabIndex]?.key === "notes" && (
                <NotesTabPanel notes={notes} />
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={preview.open}
        onClose={closePreview}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
        aria-labelledby="photo-dialog-title"
      >
        <Box sx={{ position: "relative", bgcolor: "black" }}>
          <IconButton
            aria-label="Close"
            onClick={closePreview}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              zIndex: 1,
              bgcolor: "rgba(0,0,0,0.5)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon sx={{ color: "white" }} />
          </IconButton>

          <DialogContent
            sx={{ p: 0, display: "flex", justifyContent: "center", alignItems: "center" }}
          >
            {preview.src ? (
              <Box
                component="img"
                src={preview.src}
                alt={preview.title || "Member photo"}
                sx={{
                  display: "block",
                  width: "100%",
                  maxHeight: { xs: "100vh", md: "80vh" },
                  objectFit: "contain",
                  backgroundColor: "black",
                }}
              />
            ) : (
              <Box sx={{ p: 4 }}>
                <Typography>No image available.</Typography>
              </Box>
            )}
          </DialogContent>
        </Box>
      </Dialog>
    </>
  );
};

export default MemberDetailsCard;